import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Autocomplete,
  Button,
  CircularProgress,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { format } from 'date-fns';
import {
  addNpcEntryAction,
  editNpcEntryAction,
  getNpcActivityTypes,
} from 'src/redux/actions/npcLogActions';
import { getAllPatientAction } from 'src/redux/actions/patientAction';
import { getListOfTherapistAvailability } from 'src/redux/actions/myAvailabilityAction';
import { usePermission } from 'src/hooks/use-permission';

export const patientLabel = (o: any) =>
  o ? o.name || `${o.firstName ?? ''} ${o.lastName ?? ''}`.trim() : '';

// THIN-8241: match the typed query against first name, last name, AND the full name, as a substring
// at any length. Relying on the default filter (which stringifies only getOptionLabel) made the
// search feel inconsistent — a partial or last-name-only term wouldn't reliably narrow the list.
// Explicitly stringifying all three fields makes "bell", "mart", or a last name filter correctly,
// consistent with the Patients page. `matchFrom: 'any'` (default) = substring; `trim` ignores stray
// whitespace. Exported for the colocated unit test.
export const patientFilterOptions = createFilterOptions<any>({
  trim: true,
  stringify: (o: any) => `${o?.firstName ?? ''} ${o?.lastName ?? ''} ${o?.name ?? ''}`,
});

const therapistLabel = (o: any) =>
  o ? o.name || `${o.firstName ?? ''} ${o.lastName ?? ''}`.trim() : '';

// Add/edit an NPC entry (THIN-7740). The activity-type dropdown offers ACTIVE types only
// (GET /npc-log/activity-types). Patient is optional (THIN-7741) — an NPC entry need not
// relate to a patient. THIN-8187: when opened by a reviewer (`reviewer` prop), the add form also
// shows a Therapist selector so a DOR can log time on behalf of any therapist (incl. themselves);
// that add posts through /for-therapist. A therapist's own add is unchanged (self-attributed).
const EditNpcLog = ({ open, mode, entry, onClose, reviewer = false }: any) => {
  const dispatch = useDispatch();
  const activityTypes = useSelector((s: any) => s.getNpcActivityTypesReducer);
  const patientsRes = useSelector((s: any) => s.getAllPatientReducer);
  const therapistsRes = useSelector((s: any) => s.getListOfTherapistAvaiReducer);
  const currentProviderId = useSelector(
    (s: any) => s?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const addLoading = useSelector((s: any) => s.addNpcEntryReducer?.loading);
  const editLoading = useSelector((s: any) => s.editNpcEntryReducer?.loading);

  // Reviewer add-for-team (THIN-8187): only on a reviewer-opened ADD, show + require the therapist
  // selector and fetch the facility therapist list. Editing an existing entry never reassigns it.
  // THIN-8392: EDIT never reassigns an entry (the PUT carries no therapistUuid), so the picker is
  // ADD-only; on EDIT it would default to the logged-in user and silently drop the change.
  const canEditTeamNpc = usePermission('EDIT_TEAM_NPC');
  const canPickTherapist = canEditTeamNpc && mode !== 'EDIT';

  useEffect(() => {
    if (open) {
      dispatch(getNpcActivityTypes());
      dispatch(getAllPatientAction());
      if (canPickTherapist) dispatch(getListOfTherapistAvailability());
    }
  }, [open, dispatch, canPickTherapist]);

  const today = format(new Date(), 'yyyy-MM-dd');
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      date: mode === 'EDIT' && entry?.date ? entry.date : today,
      // Default the therapist to the logged-in user so a dor+therapist can log their own time
      // without an extra pick; they can still choose any teammate.
      therapistUuid: canPickTherapist ? currentProviderId || '' : '',
      activityTypeUuid: mode === 'EDIT' ? entry?.activityTypeUuid || '' : '',
      durationMinutes: mode === 'EDIT' ? entry?.durationMinutes ?? '' : '',
      patientUuid: mode === 'EDIT' ? entry?.patientUuid || '' : '',
      comment: mode === 'EDIT' ? entry?.comment || '' : '',
    },
    validationSchema: Yup.object({
      date: Yup.string()
        .required('Date is required')
        .test(
          'not-future',
          'Date cannot be in the future',
          (v) => !v || v <= format(new Date(), 'yyyy-MM-dd')
        ),
      therapistUuid: canPickTherapist
        ? Yup.string().required('Therapist is required')
        : Yup.string().nullable(),
      activityTypeUuid: Yup.string().required('Activity type is required'),
      durationMinutes: Yup.number()
        .typeError('Duration is required')
        .positive('Duration must be greater than 0')
        .required('Duration is required'),
      comment: Yup.string().nullable(),
    }),
    onSubmit: (values) => {
      const payload = {
        date: values.date,
        activityTypeUuid: values.activityTypeUuid,
        durationMinutes: Number(values.durationMinutes),
        patientUuid: values.patientUuid || null,
        comment: values.comment || null,
      };
      if (mode === 'EDIT') {
        dispatch(editNpcEntryAction({ id: entry?.uuid, ...payload }));
      } else {
        // A therapistUuid routes the add through /for-therapist (reviewer add-for-team); its
        // absence keeps the self-attributed POST. THIN-8187.
        dispatch(
          addNpcEntryAction(
            canPickTherapist && values.therapistUuid
              ? { ...payload, therapistUuid: values.therapistUuid }
              : payload
          )
        );
      }
    },
  });

  // THIN-8172: the dialog stays mounted between opens (the parent only toggles `open`), so this
  // component's Formik state survives a Cancel/X/backdrop close and the previous entry's values
  // carry into the next Add. enableReinitialize alone can't clear it — for a repeated Add, mode
  // and entry don't change, so initialValues stay equal and Formik never re-initializes. Reset to
  // a clean slate on every open instead (blank for ADD, the entry's values for EDIT).
  useEffect(() => {
    if (open) formik.resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const types = activityTypes?.response || [];
  const patients = patientsRes?.response || [];

  // THIN-8225: the option lists carry ACTIVE types/patients only, so an existing entry whose
  // activity type was later deactivated (or whose patient left the active list) renders blank on
  // the EDIT form — and because Activity Type is required, saving would silently recategorize the
  // entry. Inject the entry's own current type/patient into the options when missing so EDIT shows
  // the real values (the type marked "(inactive)"); new entries are unaffected.
  const typeOptions =
    mode === 'EDIT' &&
    entry?.activityTypeUuid &&
    !types.some((t: any) => t.uuid === entry.activityTypeUuid)
      ? [
          ...types,
          {
            uuid: entry.activityTypeUuid,
            label: `${entry.activityTypeLabel ?? 'Activity type'} (inactive)`,
            active: false,
          },
        ]
      : types;
  const patientOptions =
    mode === 'EDIT' &&
    entry?.patientUuid &&
    !patients.some((p: any) => p.uuid === entry.patientUuid)
      ? [...patients, { uuid: entry.patientUuid, name: entry.patientName }]
      : patients;
  const selectedPatient =
    patientOptions.find((p: any) => p.uuid === formik.values.patientUuid) || null;
  const therapists = therapistsRes?.response?.data || [];
  const selectedTherapist =
    therapists.find((t: any) => t.uuid === formik.values.therapistUuid) || null;
  const saving = addLoading || editLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{mode === 'EDIT' ? 'Edit NPC Entry' : 'Add NPC Entry'}</DialogTitle>
        <DialogContent>
          <Stack
            spacing={2.5}
            sx={{ mt: 1 }}
          >
            {canPickTherapist && (
              <Autocomplete
                options={therapists}
                loading={therapistsRes?.loading}
                value={selectedTherapist}
                getOptionLabel={therapistLabel}
                isOptionEqualToValue={(o: any, v: any) => o?.uuid === v?.uuid}
                onChange={(_e, nv: any) => formik.setFieldValue('therapistUuid', nv?.uuid || '')}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Therapist"
                    error={!!(formik.touched.therapistUuid && formik.errors.therapistUuid)}
                    helperText={
                      formik.touched.therapistUuid && (formik.errors.therapistUuid as string)
                    }
                  />
                )}
              />
            )}
            <TextField
              label="Date"
              type="date"
              name="date"
              value={formik.values.date}
              onChange={formik.handleChange}
              InputLabelProps={{ shrink: true }}
              error={!!(formik.touched.date && formik.errors.date)}
              helperText={formik.touched.date && (formik.errors.date as string)}
              fullWidth
            />
            <TextField
              label="Activity Type"
              select
              name="activityTypeUuid"
              value={formik.values.activityTypeUuid}
              onChange={formik.handleChange}
              error={!!(formik.touched.activityTypeUuid && formik.errors.activityTypeUuid)}
              helperText={
                formik.touched.activityTypeUuid && (formik.errors.activityTypeUuid as string)
              }
              fullWidth
            >
              {activityTypes?.loading && (
                <MenuItem
                  disabled
                  value=""
                >
                  <CircularProgress
                    size={16}
                    sx={{ mr: 1 }}
                  />{' '}
                  Loading…
                </MenuItem>
              )}
              {typeOptions.map((t: any) => (
                <MenuItem
                  key={t.uuid}
                  value={t.uuid}
                >
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              options={patientOptions}
              loading={patientsRes?.loading}
              value={selectedPatient}
              getOptionLabel={patientLabel}
              filterOptions={patientFilterOptions}
              isOptionEqualToValue={(o: any, v: any) => o?.uuid === v?.uuid}
              onChange={(_e, nv: any) => formik.setFieldValue('patientUuid', nv?.uuid || '')}
              fullWidth
              // THIN-8241: key each option on the uuid, NOT the default (getOptionLabel). A facility
              // can hold patients with identical names (QA has several), and duplicate label-keys
              // collide in React's reconciliation, so the listbox renders stale rows and appears not
              // to filter until the input is nudged. Keying on the unique uuid fixes the stale render.
              renderOption={(props, option: any) => (
                <li
                  {...props}
                  key={option.uuid}
                >
                  {patientLabel(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Patient (optional)"
                />
              )}
            />
            <TextField
              label="Duration (minutes)"
              type="number"
              name="durationMinutes"
              value={formik.values.durationMinutes}
              onChange={formik.handleChange}
              error={!!(formik.touched.durationMinutes && formik.errors.durationMinutes)}
              helperText={
                formik.touched.durationMinutes && (formik.errors.durationMinutes as string)
              }
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="Comment"
              name="comment"
              value={formik.values.comment}
              onChange={formik.handleChange}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving…' : mode === 'EDIT' ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditNpcLog;
